/**
 * SectionSelectField
 * A dropdown field for selecting a target section on the current page.
 * Used for scroll-to-section functionality in CTA buttons.
 */

import { AlertCircle, Loader2 } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import { usePageSections } from '@/hooks/use-page-sections'
import { SECTION_REGISTRY } from '@limo/shared'

interface SectionSelectFieldProps {
  value: string | undefined
  onChange: (sectionId: string) => void
  pageId: string
  currentSectionId: string
  label: string
  description?: string
}

export function SectionSelectField({
  value,
  onChange,
  pageId,
  currentSectionId,
  label,
  description,
}: SectionSelectFieldProps) {
  const { data: sections, isLoading, error } = usePageSections(pageId)

  // Filter out the current section and get visible sections
  const availableSections = (sections || []).filter(
    (s) => s.id !== currentSectionId
  )

  // Check if the currently selected section exists in the available sections
  const selectedSectionExists = value
    ? availableSections.some((s) => s.id === value)
    : true

  // Find the selected section to get its info for display
  const selectedSection = value
    ? availableSections.find((s) => s.id === value)
    : null

  // Check if selected section is hidden
  const selectedSectionHidden = selectedSection && !selectedSection.is_visible

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Label>{label}</Label>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading sections...
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-2">
        <Label>{label}</Label>
        <div className="flex items-center gap-2 text-sm text-destructive">
          <AlertCircle className="h-4 w-4" />
          Failed to load sections
        </div>
      </div>
    )
  }

  // Empty state - no other sections available
  if (availableSections.length === 0) {
    return (
      <div className="space-y-2">
        <Label>{label}</Label>
        <p className="text-sm text-muted-foreground">
          Add more sections to this page to link to them.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Select value={value || ''} onValueChange={onChange}>
        <SelectTrigger>
          <SelectValue placeholder="Select a section..." />
        </SelectTrigger>
        <SelectContent>
          {availableSections.map((section) => {
            const definition = SECTION_REGISTRY[section.section_type]
            const sectionName = definition?.name || section.section_type
            const position = section.display_order + 1
            const isHidden = !section.is_visible

            return (
              <SelectItem key={section.id} value={section.id}>
                <span className="flex items-center gap-2">
                  <span className="text-muted-foreground text-xs">
                    #{position}
                  </span>
                  <span>{sectionName}</span>
                  {isHidden && (
                    <span className="text-xs text-amber-600 dark:text-amber-400">
                      (hidden)
                    </span>
                  )}
                </span>
              </SelectItem>
            )
          })}
        </SelectContent>
      </Select>

      {/* Warning if selected section no longer exists */}
      {value && !selectedSectionExists && (
        <p className="text-xs text-destructive flex items-center gap-1">
          <AlertCircle className="h-3 w-3" />
          Selected section no longer exists. Please select another.
        </p>
      )}

      {/* Warning if selected section is hidden */}
      {selectedSectionHidden && (
        <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
          <AlertCircle className="h-3 w-3" />
          This section is currently hidden and won't be visible to visitors.
        </p>
      )}

      {description && !value && (
        <p className="text-xs text-muted-foreground">{description}</p>
      )}
    </div>
  )
}
